import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import { gtmEvent } from 'lib/gtm'
import Box from '@material-ui/core/Box'
import Switch from '@material-ui/core/Switch'
import LinearProgress from '@material-ui/core/LinearProgress'
import { useAuth } from 'lib/auth'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import Error from 'components/error'
import { useSnackbar } from 'notistack'
import ButtonCta from 'components/atoms/button-cta'
import SocialShareDialog from 'components/social-share-dialog'
import CheckIcon from 'components/material-symbol/icons/Check'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import LibraryBooksOutlinedIcon from 'components/material-symbol/icons/LibraryBooks'
import LinkOutlinedIcon from 'components/material-symbol/icons/Link'
import ComputerOutlinedIcon from 'components/material-symbol/icons/Computer'
import useSettingsStyles, {
  SettingsFormControlLabel,
} from 'pages/pga-coach/settings/settings.styles'
import {
  SettingsLinkRow,
  SettingsSection,
  SettingsToggleRow,
} from 'pages/pga-coach/settings/settings-components'

const SUPPORT_ARTICLE_URL =
  'https://support.pga.com/en/articles/5610289-getting-started-with-pga-coach'

export const COACH_PREFERENCES = gql`
  query CoachSettings($id: ID!) {
    coach(id: $id) {
      preferences {
        bookNowEnabled
      }
    }
  }
`

const TOGGLE_BOOKNOW_PREF = gql`
  mutation UpdateBookNow($bookNow: Boolean!) {
    coachBookNowPreference(bookNowEnabled: $bookNow) {
      success
      message
      coach {
        preferences {
          bookNowEnabled
        }
      }
    }
  }
`

const BookNowToggle = ({ enabled }) => {
  const [bookNowEnabled, setState] = useState(enabled)
  const [toggle] = useMutation(TOGGLE_BOOKNOW_PREF)
  const { enqueueSnackbar } = useSnackbar()

  return (
    <SettingsFormControlLabel
      label=" "
      control={
        <Switch
          checked={bookNowEnabled}
          inputProps={{
            'aria-label': 'Display the “Book Now” button on your profile',
          }}
          onChange={event => {
            toggle({
              variables: { bookNow: event.target.checked },
            }).then(({ data: { coachBookNowPreference: op } }) => {
              enqueueSnackbar(op.message, {
                variant: op.success ? 'success' : 'error',
              })
              setState(op.coach.preferences.bookNowEnabled)
            })
          }}
        />
      }
    />
  )
}

const SetupStepRow = ({ item, classes, onStepClick }) => (
  <SettingsLinkRow
    to={item.disabled ? undefined : item.link}
    onClick={item.disabled ? undefined : () => onStepClick(item)}
    icon={item.isCompleted ? <CheckIcon /> : item.icon}
    iconWrapClassName={
      item.isCompleted ? classes.rowIconCompleted : classes.rowIconTinted
    }
    title={item.title}
    description={item.isCompleted ? undefined : item.description}
    trailing={item.trailing}
    badge={item.isCompleted ? 'Done' : undefined}
    disabled={item.disabled}
    disabledTooltip={item.disabledTooltip}
    data-testid={item.testId}
    classes={classes}
  />
)

const ScheduleOnboarding = ({ onboardingSchedule }) => {
  const classes = useSettingsStyles()
  const hasFacility = !!onboardingSchedule?.isFacilitiesCreated
  const hasAvailability = !!onboardingSchedule?.isAvailable
  const hasCoachProfile = !!onboardingSchedule?.hasCoachProfile
  const settingsBlocks = [
    {
      title: 'Set your facility',
      blocks: [
        {
          link: '/pga-coach/facilities/new',
          disabled: false,
          isCompleted: hasFacility,
          icon: <RoomOutlinedIcon />,
          testId: 'create-facility',
          title: 'Where are you hosting your offerings?',
          description:
            'Associate a facility to your account. This facility’s address will also help golfers find your Coach Profile on PGA.com.',
          gtmEvent: {
            event: 'onboarding-click',
            onboardingCategory: 'onboarding-schedule',
            onboardingLabel: 'create-facility',
            onboardingAction: 'click-create-facility',
          },
        },
      ],
    },
    {
      title: 'Set up a group offering',
      blocks: [
        {
          link: '/pga-coach/bookings/events/new',
          disabled: !hasFacility,
          disabledTooltip: 'Add a facility first.',
          isCompleted: onboardingSchedule?.isGroupEventCreated,
          icon: <CelebrationOutlinedIcon />,
          testId: 'create-group-event',
          title: 'Publish your first group event',
          description:
            'Start with a template or create your own. Some examples are Whiskey & Wedges, 9-Holes with a PGA Coach and Night Glow Golf. If you want to add another one in the future, use the My Offerings link.',
          gtmEvent: {
            event: 'onboarding-click',
            onboardingCategory: 'onboarding-schedule',
            onboardingLabel: 'create-group-event',
            onboardingAction: 'click-create-group-event',
          },
        },
      ],
    },
    {
      title: 'Create a 1:1 private offering',
      blocks: [
        {
          link: '/pga-coach/settings/availability',
          disabled: !hasFacility,
          disabledTooltip: 'Add a facility first.',
          isCompleted: hasAvailability,
          icon: <EventAvailableOutlinedIcon />,
          testId: 'enable-availability',
          title: 'Set your working hours',
          description:
            'Private sessions will only be offered within your available working hours at your facility. Set your availability before you create your booking type.',
          gtmEvent: {
            event: 'onboarding-click',
            onboardingCategory: 'onboarding-schedule',
            onboardingLabel: 'enable-availability',
            onboardingAction: 'click-enable-availability',
          },
        },
        {
          link: '/pga-coach/bookings/booking-types/new',
          disabled: !hasAvailability || !hasCoachProfile,
          disabledTooltip: !hasCoachProfile
            ? 'Create your Coach Profile first.'
            : 'Set your working hours first.',
          isCompleted: onboardingSchedule?.isBookingTypeCreated,
          icon: <LibraryBooksOutlinedIcon />,
          testId: 'create-booking-type',
          title: 'Publish your first booking type',
          description: hasCoachProfile
            ? 'Choose from predefined categories like Intro, Private and Swing Evaluation, or create your own. If you want to manage your booking types or add more in the future, use the My Offerings link.'
            : 'Create your Coach Profile before you publish a booking type.',
          trailing: !hasCoachProfile && (
            <ButtonCta variant="text" arrow href="/pga-coach/profile/">
              Coach Profile
            </ButtonCta>
          ),
          gtmEvent: {
            event: 'onboarding-click',
            onboardingCategory: 'onboarding-schedule',
            onboardingLabel: 'create-booking-type',
            onboardingAction: 'click-create-booking-type',
          },
        },
      ],
    },
  ]

  const {
    user: {
      coach: { id: coachId },
    },
    user,
    setUserProperties,
  } = useAuth()

  const { data, loading, error } = useQuery(COACH_PREFERENCES, {
    variables: { id: coachId },
  })

  const handleStepClick = item => {
    gtmEvent(item.gtmEvent)
    setUserProperties({ onboarding: true })
  }

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const bookNowEnabled = !!data?.coach?.preferences?.bookNowEnabled
  const bookingUrl = user?.coach?.bookingUrl

  return (
    <Box className={classes.contentContainer}>
      <TipWell
        icon={InfoOutlinedIcon}
        caption="If you need assistance setting up your PGA Schedule, dive into our support articles for expert guidance."
        action={
          <TipWellActionButton
            href={SUPPORT_ARTICLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
          >
            Learn More
          </TipWellActionButton>
        }
      />
      {settingsBlocks.map(block => (
        <SettingsSection
          key={block.title}
          eyebrow={block.title}
          classes={classes}
        >
          {block.blocks.map(item => (
            <SetupStepRow
              key={item.title}
              item={item}
              classes={classes}
              onStepClick={handleStepClick}
            />
          ))}
        </SettingsSection>
      ))}
      <SettingsSection
        eyebrow="Ready to share your offerings?"
        classes={classes}
      >
        <SocialShareDialog
          trigger={
            <SettingsLinkRow
              icon={<LinkOutlinedIcon />}
              iconWrapClassName={classes.rowIconTinted}
              title="Share booking link with your students"
              description="Copy your Coach Offerings link and share it via text, email, or social media."
              data-testid="share-booking-link"
              classes={classes}
            />
          }
          shareUrl={bookingUrl}
          shareTitle="My Offerings"
          showBookingUrl={true}
          att="booking-link"
        />
        <SettingsToggleRow
          icon={<ComputerOutlinedIcon />}
          iconClassName={`${classes.rowIcon} ${classes.rowIconTinted}`}
          title="Display the “Book Now” button on your profile"
          description="Your offerings are shareable already, but they’re not available to the public via your Coach Profile until you enable this button."
          badge={bookNowEnabled ? 'On' : 'Off'}
          action={<BookNowToggle enabled={bookNowEnabled} />}
          classes={classes}
        />
      </SettingsSection>
    </Box>
  )
}

export default ScheduleOnboarding
